import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import { Eye, Globe, MapPin, Search } from "lucide-react";

import PaginationLayout from "../components/PaginationLayout";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

interface driverDetail {
  name: string;
}
interface ridebook {
  city_from: string;
  booking_status: string;
  city_to: string;
  total_price: number;
  booked_seats: number;
  driver_detail: driverDetail;
  id: string;
  user_id?: any;
  address_from?: string;
  address_to?: string;
  created_at?: string;
  date?: string;
}

function SavedRides({ _id }: any) {
  const [page, setPage] = useState<number>(1);
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({
    data: {
      data: [] as Array<ridebook>,
      total_count: 0,
    },
  });
  const [loading, setLoading] = useState(false);
  const newParam = new URLSearchParams(location.search);
  const limit = 10;
  const { authState } = useContext(GlobalContext);
  const [details, setDetails] = useState({} as any);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const onChangePagination = (newVal: number) => {
    setPage(Number(newVal));
  };

  const q = newParam.toString();
  const getSavedRides = async () => {
    setLoading(true);
    try {
      const bookDetail = await henceforthApi.User.savedRides(
        q,
        page - 1,
        limit
      );
      setState(bookDetail);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const showDetails = (res: any) => {
    setDetails(res);
  };

  const handleCountryFilter = (val: string) => {
    if (val && val !== "ALL") {
      newParam.set("country", val);
    } else {
      newParam.delete("country");
    }
    navigate({ pathname: "/saved-rides/1", search: newParam.toString() });
  };

  useEffect(() => {
    getSavedRides();
  }, [page, newParam.get("country")]);

  const currentCountry = newParam.get("country") || "ALL";

  // AG Grid Column Definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => {
          return (page - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "User",
        field: "user_id.name",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const user = p.data?.user_id;
          const imgSrc =
            user?.profile_pic && user?.profile_pic !== "null"
              ? henceforthApi.FILES.imageOriginal(user.profile_pic)
              : profile_placeholder;
          return (
            <div className="flex items-center gap-2.5 py-1">
              <img
                src={imgSrc}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = profile_placeholder;
                }}
              />
              <span className="font-semibold text-slate-800 text-sm leading-tight hover:text-[#006747] transition-colors truncate">
                {user?.name || "Not Available"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Email",
        field: "user_id.email",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-700 text-sm truncate">
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Country",
        field: "user_id.country",
        width: 100,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-600 text-sm">
              {p.value || "N/A"}
            </span>
          );
        },
      },
      {
        headerName: "Pickup Location",
        field: "address_from",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const val = p.value || "Not available";
          return (
            <div className="flex items-center gap-1.5 text-slate-700 truncate text-sm" title={val}>
              <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="truncate">{val}</span>
            </div>
          );
        },
      },
      {
        headerName: "Dropoff Location",
        field: "address_to",
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => {
          const val = p.value || "Not available";
          return (
            <div className="flex items-center gap-1.5 text-slate-700 truncate text-sm" title={val}>
              <MapPin className="w-4 h-4 text-rose-500 shrink-0" />
              <span className="truncate">{val}</span>
            </div>
          );
        },
      },
      {
        headerName: "Date",
        field: "created_at",
        width: 120,
        valueFormatter: (p) => {
          return p.value
            ? moment(Number(p.value)).format("DD MMM YYYY")
            : "N/A";
        },
      },
      {
        headerName: "Phone No.",
        field: "user_id.phone_no",
        width: 125,
        cellRenderer: (p: any) => {
          const user = p.data?.user_id;
          if (!user?.phone_no || user?.phone_no === 0) return <span className="text-slate-400 text-sm">N/A</span>;
          return <span className="text-sm">{`${user?.country_code || ""} ${user?.phone_no}`}</span>;
        },
      },
      {
        headerName: "Actions",
        minWidth: 70,
        maxWidth: 85,
        width: 75,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => {
          return (
            <div className="flex items-center justify-center w-full">
              <button
                type="button"
                onClick={() => showDetails(p.data)}
                data-bs-toggle="modal"
                data-bs-target="#savedContent"
                title="View Details"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#006747] hover:bg-[#006747]/10 transition-colors cursor-pointer"
              >
                <Eye className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ];
  }, [page]);

  return (
    <>
      {/* Compact Single Header Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {/* Left: Search Bar */}
        <div className="relative w-56 sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            name="search"
            defaultValue={newParam.get("search") || ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val) {
                newParam.set("search", val);
              } else {
                newParam.delete("search");
              }
              navigate({ pathname: `/saved-rides/1`, search: newParam.toString() });
            }}
            placeholder="Search saved rides..."
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Country Filter */}
          <div className="w-40">
            <Select
              value={currentCountry}
              onValueChange={handleCountryFilter}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <div className="flex items-center gap-1.5 truncate">
                  <Globe className="w-4 h-4 text-slate-400" />
                  <SelectValue placeholder="Country" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Countries</SelectItem>
                <SelectItem value="CA">Canada</SelectItem>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="NG">Nigeria</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={state.data.data}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No saved rides found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={state.data.total_count}
          data={state.data.data}
          page={page}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => onChangePagination(val)}
        />
      </div>

      {/* Details Modal */}
      <div
        className="modal fade"
        id="savedContent"
        tabIndex={-1}
        aria-labelledby="savedContentLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded-xl border border-slate-200 shadow-lg overflow-hidden">
            <div className="modal-header bg-slate-50 border-b border-slate-100 px-5 py-3">
              <h5 className="m-0 text-base font-bold text-slate-800">
                Ride Details
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-5 space-y-3 text-xs text-slate-700">
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Passenger Name</span>
                <span className="font-semibold text-slate-900">
                  {details?.user_id?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Email</span>
                <span className="font-semibold text-slate-900">
                  {details?.user_id?.email || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Country</span>
                <span className="font-semibold text-slate-900">
                  {details?.user_id?.country || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Pick Up Location</span>
                <span className="font-semibold text-slate-900 text-right max-w-[240px]">
                  {details?.address_from || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Drop Off Location</span>
                <span className="font-semibold text-slate-900 text-right max-w-[240px]">
                  {details?.address_to || "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-100">
                <span className="text-slate-500 font-medium">Date</span>
                <span className="font-semibold text-slate-900">
                  {details?.created_at ? moment(Number(details.created_at)).format("DD MMM YYYY") : "N/A"}
                </span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-slate-500 font-medium">Phone No.</span>
                <span className="font-semibold text-slate-900">
                  {details?.user_id?.phone_no ? `${details?.user_id?.country_code || ""} ${details?.user_id?.phone_no}` : "N/A"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SavedRides;
